/**
 * receiptPrinter.js
 * -----------------
 * Thermal receipt printing for the Chip Bank cage.
 *
 * Target hardware: Epson TM-T88VII (80mm thermal receipt printer).
 *
 * Two print paths are provided:
 *
 *  1. eposPrintReceipt() — PRIMARY. Sends the receipt straight to the
 *     printer over the network using Epson's ePOS-Print Service (an XML
 *     job POSTed to the printer's built-in web service). This never
 *     touches the browser's print pipeline, so there is no print dialog
 *     and — crucially — no --kiosk-printing switch, which would silently
 *     auto-print every other document on the workstation too. Requires
 *     the printer's IP / host to be configured in cage settings.
 *
 *  2. printReceipt() — FALLBACK. Renders each receipt into a hidden
 *     iframe and prints it through the browser / OS driver. Used when no
 *     printer IP is configured, or when an ePOS request fails.
 *
 * Either way, every copy is cut as its own slip rather than printing as
 * one long uncut strip.
 */

import { brand } from '../brand';

export const RECEIPT_BUSINESS = {
  name: brand?.naming?.appName || 'EDH Casino',
  subtitle: brand?.naming?.playersClub || "Player's Club",
  website: (brand?.contact?.website || 'edhcasino.com').replace(/^https?:\/\//, ''),
};

const COPY_MIN = 1;
const COPY_MAX = 4;
const COPY_LABELS = ['GUEST COPY', 'CAGE COPY'];

function fmtCents(cents) {
  const n = Number(cents);
  if (cents == null || Number.isNaN(n)) return '$0.00';
  return (n / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

// Signed amount for the prominent total at the foot of the receipt:
// "+$100.00" for a deposit, "-$100.00" for a withdrawal.
function fmtSignedCents(cents, isDeposit) {
  return `${isDeposit ? '+' : '-'}${fmtCents(cents)}`;
}

// Cashier attribution. Originals carry the employee number from the PIN
// entry; reprints (which don't re-collect a PIN) fall back to the banker's
// name captured on the transaction.
function cashierValue(receipt) {
  if (receipt.cashierEmployeeNumber) return 'Emp #' + receipt.cashierEmployeeNumber;
  return receipt.cashierName || '-';
}

function fmtDateTime(value) {
  const d = value ? new Date(value) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return safe.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function shortRef(ref) {
  if (!ref) return '-';
  const s = String(ref).replace(/-/g, '').toUpperCase();
  return s.length > 8 ? s.slice(0, 8) : s;
}

function clampCopies(copies) {
  const n = parseInt(copies, 10);
  if (!Number.isInteger(n)) return COPY_MIN;
  return Math.max(COPY_MIN, Math.min(COPY_MAX, n));
}

function copyLabel(index, total) {
  if (!total || total <= 1) return '';
  if (index < COPY_LABELS.length) return COPY_LABELS[index];
  return `COPY ${index + 1} OF ${total}`;
}

function row(label, value, opts) {
  const o = opts || {};
  const cls = ['row', o.bold ? 'bold' : '', o.big ? 'big' : '']
    .filter(Boolean)
    .join(' ');
  return (
    `<div class="${cls}">` +
    `<span class="lbl">${escapeHtml(label)}</span>` +
    `<span class="val">${escapeHtml(value)}</span>` +
    `</div>`
  );
}

/**
 * Build the inner markup for a single receipt copy.
 */
function receiptSection(receipt, label) {
  const isDeposit = receipt.type === 'deposit';
  const isDraw = receipt.type === 'credit_draw';
  const isCredit = isDraw || receipt.type === 'credit_payment';

  const docTitle = isCredit ? 'Credit Line Receipt' : "Player's Bank Receipt";
  const txnTitle = isCredit
    ? isDraw
      ? 'CREDIT DRAWN'
      : 'BALANCE PAID'
    : isDeposit
      ? 'DEPOSIT'
      : 'WITHDRAWAL';

  const parts = [];
  if (label) {
    parts.push(`<div class="copylabel">** ${escapeHtml(label)} **</div>`);
  }
  if (receipt.reprint) {
    parts.push(`<div class="copylabel">*** REPRINT ***</div>`);
  }
  parts.push(
    `<div class="brand">${escapeHtml(RECEIPT_BUSINESS.name).toUpperCase()}</div>`
  );
  parts.push(`<div class="sub">${escapeHtml(RECEIPT_BUSINESS.subtitle)}</div>`);
  parts.push(`<div class="doctitle">${escapeHtml(docTitle)}</div>`);
  parts.push(`<div class="rule"></div>`);
  parts.push(`<div class="txntype">${escapeHtml(txnTitle)}</div>`);
  parts.push(`<div class="rule"></div>`);
  parts.push(row('Date', fmtDateTime(receipt.dateTime)));
  parts.push(row('Txn #', shortRef(receipt.reference)));
  parts.push(row('Player', receipt.playerName || 'Player'));
  if (receipt.memberNumber) {
    parts.push(row('Club #', String(receipt.memberNumber)));
  }
  parts.push(`<div class="rule dashed"></div>`);

  if (isCredit) {
    // Credit line: show the amount moved, remaining balance owed, and the
    // available credit that remains — no signed deposit/withdrawal total.
    parts.push(
      row(isDraw ? 'Credit drawn' : 'Balance paid', fmtCents(receipt.amountCents), {
        bold: true,
        big: true,
      })
    );
    parts.push(row('Balance owed', fmtCents(receipt.balanceOwedCents)));
    parts.push(row('Available credit', fmtCents(receipt.availableCreditCents)));
    parts.push(`<div class="rule"></div>`);
    parts.push(row('Cashier', cashierValue(receipt)));
  } else {
    const amountLabel = isDeposit ? 'Amount deposited' : 'Amount withdrawn';
    const balanceLabel = isDeposit ? 'New balance' : 'Remaining balance';
    parts.push(row(amountLabel, fmtCents(receipt.amountCents)));
    parts.push(`<div class="rule dashed"></div>`);
    parts.push(
      row(balanceLabel, fmtCents(receipt.balanceAfterCents), {
        bold: true,
        big: true,
      })
    );
    parts.push(`<div class="rule"></div>`);
    parts.push(row('Cashier', cashierValue(receipt)));
    parts.push(
      `<div class="bigamount">${escapeHtml(
        fmtSignedCents(receipt.amountCents, isDeposit)
      )}</div>`
    );
  }

  parts.push(
    `<div class="footer">Please keep this receipt<br/>for your records.</div>`
  );
  parts.push(
    `<div class="website">${escapeHtml(RECEIPT_BUSINESS.website)}</div>`
  );

  return `<div class="receipt">${parts.join('')}</div>`;
}

const RECEIPT_CSS = `
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    font-family: 'Menlo', 'Consolas', 'Courier New', monospace;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .receipt {
    width: 72mm;
    margin: 0 auto;
    padding: 4mm 4mm 7mm;
    font-size: 12px;
    line-height: 1.45;
  }
  .copylabel {
    text-align: center;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 1px;
    margin-bottom: 5px;
  }
  .brand {
    text-align: center;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 2px;
  }
  .sub {
    text-align: center;
    font-size: 10px;
    letter-spacing: 3px;
    text-transform: uppercase;
  }
  .doctitle { text-align: center; font-size: 12px; margin-top: 3px; }
  .txntype {
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 1px 0;
  }
  .rule { border-top: 1px solid #000; margin: 5px 0; }
  .rule.dashed { border-top: 1px dashed #000; }
  .row { display: flex; justify-content: space-between; gap: 10px; }
  .row .val { text-align: right; white-space: nowrap; }
  .row .lbl { word-break: break-word; }
  .row.bold { font-weight: 700; }
  .row.big { font-size: 14px; }
  .bigamount {
    margin-top: 9px;
    padding: 6px 4px;
    border: 2px solid #000;
    border-radius: 3px;
    text-align: center;
    font-size: 23px;
    font-weight: 700;
    letter-spacing: 1px;
  }
  .footer { text-align: center; margin-top: 10px; font-size: 11px; }
  .website {
    text-align: center;
    margin-top: 6px;
    font-size: 11px;
    font-weight: 700;
  }
`;

/**
 * Build a self-contained HTML document for a single receipt copy.
 * `copyIndex` / `copyTotal` drive the GUEST COPY / CAGE COPY label.
 * Exported so the cage UI can render a faithful print preview.
 */
export function buildReceiptHTML(receipt, options) {
  const opts = options || {};
  const total = clampCopies(opts.copyTotal || opts.copies || 1);
  const index = Number.isInteger(opts.copyIndex) ? opts.copyIndex : 0;
  const inner = receiptSection(receipt, copyLabel(index, total));
  return (
    '<!doctype html><html><head><meta charset="utf-8" />' +
    `<title>Player's Bank Receipt</title><style>${RECEIPT_CSS}</style>` +
    `</head><body>${inner}</body></html>`
  );
}

/**
 * Render one receipt document into a hidden iframe and print it.
 * Resolves once the print job has been dispatched.
 */
function printOneDocument(html) {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, {
      position: 'fixed',
      right: '0',
      bottom: '0',
      width: '0',
      height: '0',
      border: '0',
      visibility: 'hidden',
    });
    document.body.appendChild(iframe);

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      setTimeout(() => {
        try {
          iframe.remove();
        } catch (e) {
          /* already detached */
        }
      }, 500);
      resolve();
    };

    const win = iframe.contentWindow;
    const doc = win.document;
    doc.open();
    doc.write(html);
    doc.close();

    win.onafterprint = finish;

    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch (e) {
        console.error('[receiptPrinter] print failed', e);
      }
      setTimeout(finish, 900);
    }, 250);
  });
}

/**
 * Print `copies` receipts. Each copy is sent as its own print job so the
 * TM-T88VII cuts the paper at the end of every receipt. Returns a
 * promise resolving once all copies have been dispatched.
 */
export async function printReceipt(receipt, copies) {
  if (!receipt) return false;
  const total = clampCopies(copies || 1);
  for (let i = 0; i < total; i += 1) {
    const html = buildReceiptHTML(receipt, { copyIndex: i, copyTotal: total });
    // eslint-disable-next-line no-await-in-loop
    await printOneDocument(html);
  }
  return true;
}

// =============================================================
// ePOS-Print direct network printing (PRIMARY path)
// =============================================================
// The TM-T88VII ships with an ePOS-Print Service: an HTTP(S) endpoint
// on the printer that accepts a SOAP/XML print job and drives the
// printer directly. This bypasses the browser print pipeline entirely
// — no print dialog, no --kiosk-printing switch — so ordinary printing
// on the cage workstation is left completely untouched.
//
// One-time setup on the printer / cage PC:
//   * Give the printer a static IP on the LAN.
//   * The ePOS service answers on HTTPS with a self-signed certificate;
//     open https://<printer-ip>/ once in the cage browser and accept
//     the certificate so subsequent requests succeed.

// Printable line width in monospace columns. The cage's TM-T88VII
// fits 42 Font-A columns per line — a 48-column assumption overflowed
// and every row wrapped onto a second line. Rows pad to this width.
const RECEIPT_COLS = 42;

/**
 * Reduce a string to plain printable ASCII. Thermal printers fall back
 * to their built-in code page, which mangles curly quotes, em-dashes
 * and other typographic characters — so normalize and strip them.
 */
function asciiize(value) {
  return String(value == null ? '' : value)
    .normalize('NFKD')
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[–—−]/g, '-')
    .replace(/[^\x20-\x7E]/g, '');
}

/**
 * Build a 48-column row with `left` flush-left and `right` flush-right.
 * If the two would collide, the label is truncated so the value (the
 * important part) always prints in full.
 */
function padRow(left, right) {
  const l = asciiize(left);
  const r = asciiize(right);
  if (l.length + r.length + 1 > RECEIPT_COLS) {
    const room = Math.max(0, RECEIPT_COLS - r.length - 1);
    return (l.slice(0, room) + ' ' + r).slice(0, RECEIPT_COLS);
  }
  return l + ' '.repeat(RECEIPT_COLS - l.length - r.length) + r;
}

/**
 * One ePOS <text> element: a line of ASCII-safe, XML-escaped text
 * terminated by a line feed.
 */
function eposText(text) {
  return '<text>' + escapeHtml(asciiize(text)) + '&#10;</text>';
}

// Friendly messages for the ePOS-Print result codes the cage is most
// likely to hit. Anything not listed falls back to a generic message.
const EPOS_FRIENDLY = {
  EX_ENPC: 'Could not reach the printer on the network.',
  EX_BADPORT: 'Could not open the printer port.',
  EX_TIMEOUT: 'The printer did not respond in time.',
  DeviceNotFound:
    'No printer answered at this address. Check the printer IP / host.',
  PrintSystemError: 'The printer reported a hardware error.',
  EPTR_COVER_OPEN: 'The printer cover is open.',
  EPTR_REC_EMPTY: 'The printer is out of paper.',
  EPTR_UNRECOVERABLE:
    'The printer reported an unrecoverable error — power-cycle it.',
  EPTR_AUTOMATICAL: 'The printer reported a mechanical error (paper jam?).',
  SchemaError: 'The receipt data was rejected by the printer.',
  DeviceInUse: 'The printer is busy with another job. Try again.',
};

/**
 * A full-width horizontal rule. Uses the printer's native <hline>
 * element so it prints as a clean drawn line rather than a row of
 * ASCII dashes. x2 is set generously; the printer clips it to the
 * actual print-area width, so the rule always spans the full row.
 */
function eposRule() {
  return '<hline x1="0" x2="575" style="medium"/>';
}

/**
 * Centre `text` within `width` columns by padding both sides with
 * spaces. Used for the full-width reverse-video bars, where the padded
 * spaces print as part of the black bar.
 */
function padCenter(text, width) {
  const t = asciiize(text);
  if (t.length >= width) return t.slice(0, width);
  const pad = width - t.length;
  const left = Math.floor(pad / 2);
  return ' '.repeat(left) + t + ' '.repeat(pad - left);
}

/**
 * Build the ePOS-Print command sequence for one receipt copy: a
 * reverse-video copy banner, the masthead, a transaction banner, detail
 * rows separated by hairline rules, the signed amount as a bold
 * reverse-video bar, and a footer — finished with a paper cut.
 */
function eposReceiptCommands(receipt, label) {
  const isDeposit = receipt.type === 'deposit';
  const isDraw = receipt.type === 'credit_draw';
  const isCredit = isDraw || receipt.type === 'credit_payment';
  const docTitle = isCredit ? 'Credit Line Receipt' : "Player's Bank Receipt";
  const txnTitle = isCredit
    ? isDraw
      ? 'CREDIT DRAWN'
      : 'BALANCE PAID'
    : isDeposit
      ? 'DEPOSIT'
      : 'WITHDRAWAL';

  const out = [];
  out.push('<feed line="1"/>');

  // Copy banner (GUEST COPY / CAGE COPY) — full-width reverse bar.
  if (label) {
    out.push('<text align="center"/>');
    out.push('<text reverse="true" em="true"/>');
    out.push(eposText(padCenter(label, RECEIPT_COLS)));
    out.push('<text reverse="false" em="false"/>');
    out.push('<feed line="1"/>');
  }

  // Reprint marker so a duplicate is never mistaken for an original.
  if (receipt.reprint) {
    out.push('<text align="center" em="true"/>');
    out.push(eposText('*** REPRINT ***'));
    out.push('<text em="false"/>');
    out.push('<feed line="1"/>');
  }

  // Masthead.
  out.push('<text align="center"/>');
  out.push('<text width="2" height="2" em="true"/>');
  out.push(eposText(RECEIPT_BUSINESS.name.toUpperCase()));
  out.push('<text width="1" height="1" em="false"/>');
  out.push(eposText(RECEIPT_BUSINESS.subtitle.toUpperCase()));
  out.push('<text em="true"/>');
  out.push(eposText(docTitle));
  out.push('<text em="false"/>');
  out.push('<feed line="1"/>');

  // Transaction-type banner — double-height, between hairline rules.
  out.push(eposRule());
  out.push('<text align="center"/>');
  out.push('<text height="2" em="true"/>');
  out.push(eposText(txnTitle));
  out.push('<text height="1" em="false"/>');
  out.push(eposRule());
  out.push('<feed line="1"/>');

  // Detail rows.
  out.push('<text align="left"/>');
  out.push(eposText(padRow('Date', fmtDateTime(receipt.dateTime))));
  out.push(eposText(padRow('Txn #', shortRef(receipt.reference))));
  out.push(eposText(padRow('Player', receipt.playerName || 'Player')));
  if (receipt.memberNumber) {
    out.push(eposText(padRow('Club #', String(receipt.memberNumber))));
  }
  out.push('<feed line="1"/>');
  out.push(eposRule());

  if (isCredit) {
    // Credit line: amount moved (emphasized), remaining balance owed, and
    // available credit. No signed deposit/withdrawal total bar.
    out.push('<text em="true"/>');
    out.push(
      eposText(
        padRow(isDraw ? 'Credit drawn' : 'Balance paid', fmtCents(receipt.amountCents))
      )
    );
    out.push('<text em="false"/>');
    out.push(eposText(padRow('Balance owed', fmtCents(receipt.balanceOwedCents))));
    out.push(eposText(padRow('Available credit', fmtCents(receipt.availableCreditCents))));
    out.push(eposRule());
    out.push(eposText(padRow('Cashier', cashierValue(receipt))));
  } else {
    const amountLabel = isDeposit ? 'Amount deposited' : 'Amount withdrawn';
    const balanceLabel = isDeposit ? 'New balance' : 'Remaining balance';
    out.push(eposText(padRow(amountLabel, fmtCents(receipt.amountCents))));
    out.push(eposRule());
    out.push('<text em="true"/>');
    out.push(eposText(padRow(balanceLabel, fmtCents(receipt.balanceAfterCents))));
    out.push('<text em="false"/>');
    out.push(eposRule());
    out.push(eposText(padRow('Cashier', cashierValue(receipt))));

    // Signed amount (+$100.00 / -$100.00) — bold reverse-video bar.
    out.push('<feed line="1"/>');
    out.push('<text align="center"/>');
    out.push('<text reverse="true" width="2" height="2" em="true"/>');
    out.push(
      eposText(
        padCenter(
          fmtSignedCents(receipt.amountCents, isDeposit),
          RECEIPT_COLS / 2
        )
      )
    );
    out.push('<text reverse="false" width="1" height="1" em="false"/>');
  }

  // Footer.
  out.push('<feed line="1"/>');
  out.push('<text align="center"/>');
  out.push(eposText('Please keep this receipt'));
  out.push(eposText('for your records.'));
  out.push('<text em="true"/>');
  out.push(eposText(RECEIPT_BUSINESS.website));
  out.push('<text em="false"/>');
  out.push('<text align="left"/>');
  out.push('<feed line="1"/>');
  out.push('<cut type="feed"/>');

  return out.join('');
}

/**
 * Wrap N receipt copies in the ePOS-Print SOAP envelope. Each copy ends
 * in its own <cut>, so copies come out as separate slips.
 *
 * `options.label`, when provided, overrides the per-copy GUEST/CAGE
 * banner with a fixed label — used for test prints.
 */
function buildEposBody(receipt, copies, options) {
  const opts = options || {};
  const total = clampCopies(copies || 1);
  let commands = '';
  for (let i = 0; i < total; i += 1) {
    const label = opts.label != null ? opts.label : copyLabel(i, total);
    commands += eposReceiptCommands(receipt, label);
  }
  return (
    '<?xml version="1.0" encoding="utf-8"?>' +
    '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">' +
    '<s:Body>' +
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">' +
    commands +
    '</epos-print>' +
    '</s:Body>' +
    '</s:Envelope>'
  );
}

/**
 * Pull `success` / `code` out of the printer's ePOS-Print response.
 * Regex-based to sidestep XML-namespace handling in DOMParser.
 */
function parseEposResponse(xml) {
  const text = String(xml == null ? '' : xml);
  const successMatch = text.match(/success="(true|false)"/i);
  const codeMatch = text.match(/code="([^"]*)"/i);
  return {
    success: !!successMatch && successMatch[1].toLowerCase() === 'true',
    code: codeMatch ? codeMatch[1] : '',
  };
}

/**
 * Print `copies` receipts directly to the TM-T88VII via the ePOS-Print
 * Service. Resolves to { ok: true } on success, or
 * { ok: false, error, code? } with an operator-friendly message.
 *
 * `printerHost` is the printer's IP or hostname (optionally with a
 * port), e.g. "192.168.1.50". `options.label` overrides the per-copy
 * GUEST/CAGE banner — used for test prints.
 */
export async function eposPrintReceipt(receipt, copies, printerHost, options) {
  if (!receipt) return { ok: false, error: 'No receipt to print.' };
  const host = String(printerHost == null ? '' : printerHost).trim();
  if (!host) {
    return {
      ok: false,
      error: 'No printer IP / host is configured in cage settings.',
    };
  }

  const url =
    'https://' +
    host +
    '/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000';
  const body = buildEposBody(receipt, copies, options);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/xml; charset=utf-8',
        'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT',
        SOAPAction: '""',
      },
      body,
      signal: controller.signal,
    });

    if (!res.ok) {
      return {
        ok: false,
        error: 'The printer service returned HTTP ' + res.status + '.',
      };
    }

    const xml = await res.text();
    const parsed = parseEposResponse(xml);
    if (parsed.success) return { ok: true };

    return {
      ok: false,
      code: parsed.code,
      error:
        EPOS_FRIENDLY[parsed.code] ||
        'The printer rejected the job' +
          (parsed.code ? ' (' + parsed.code + ')' : '') +
          '.',
    };
  } catch (err) {
    if (err && err.name === 'AbortError') {
      return {
        ok: false,
        error:
          'The printer did not respond. Check that it is powered on and ' +
          'reachable at ' +
          host +
          '.',
      };
    }
    return {
      ok: false,
      error:
        'Could not reach the printer at ' +
        host +
        '. If it uses HTTPS with a self-signed certificate, open ' +
        'https://' +
        host +
        '/ once in this browser, accept the certificate, then retry.',
    };
  } finally {
    clearTimeout(timer);
  }
}
